"use strict";
/**
 * API helper functions for DegreePlan endpoints
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.degreePlanApi = void 0;
const apiClient_1 = require("./apiClient");
const validation_1 = require("./validation");
const apiMappers_1 = require("./apiMappers");
const buildExistingCohortAddPayload = (cohortPlan, studentIds) => {
    var _a, _b;
    const organization = cohortPlan.organization;
    const cohort = cohortPlan.cohort;
    const tenantId = cohortPlan.tenantId || (organization === null || organization === void 0 ? void 0 : organization.tenantId) || (cohort === null || cohort === void 0 ? void 0 : cohort.tenantId);
    const academicProgramId = ((_a = cohortPlan.academicProgram) === null || _a === void 0 ? void 0 : _a.id) || ((_b = cohort === null || cohort === void 0 ? void 0 : cohort.academicProgram) === null || _b === void 0 ? void 0 : _b.id);
    const sortedTerms = [...(cohortPlan.terms || [])].sort((a, b) => a.position - b.position);
    return {
        studentIds,
        cohortDto: {
            cohort: {
                organizationId: organization === null || organization === void 0 ? void 0 : organization.id,
                academicProgramId,
                tenantId,
                program: cohort === null || cohort === void 0 ? void 0 : cohort.program,
                track: cohort === null || cohort === void 0 ? void 0 : cohort.track,
                specialization: cohort === null || cohort === void 0 ? void 0 : cohort.specialization,
                cohortId: cohort === null || cohort === void 0 ? void 0 : cohort.id,
            },
        },
        degreePlanDto: {
            degreePlan: {
                degreePlanId: cohortPlan.id,
                type: cohortPlan.type,
                academicProgramId,
                graduationDate: cohortPlan.graduationDate,
                tenantId,
                organizationId: organization === null || organization === void 0 ? void 0 : organization.id,
                studentProgramId: cohortPlan.studentProgramId,
            },
            degreePlanTermDtos: sortedTerms.map((degreePlanTerm) => ({
                degreePlanTerm: {
                    termId: degreePlanTerm.term.id,
                    position: degreePlanTerm.position,
                },
                degreePlanTermCourseDtos: degreePlanTerm.courses.map((degreePlanTermCourse) => ({
                    degreePlanTermCourse: { courseId: degreePlanTermCourse.course.id },
                })),
            })),
        },
    };
};
const buildStudentUpdatePayload = (plan) => {
    var _a, _b, _c, _d;
    const sortedTerms = [...(plan.terms || [])].sort((a, b) => a.position - b.position);
    const tenantId = plan.tenantId || ((_a = plan.organization) === null || _a === void 0 ? void 0 : _a.tenantId) || ((_b = plan.cohort) === null || _b === void 0 ? void 0 : _b.tenantId);
    return {
        degreePlan: {
            degreePlanId: plan.id,
            cohortId: (_c = plan.cohort) === null || _c === void 0 ? void 0 : _c.id,
            studentProgramId: plan.studentProgramId,
            type: plan.type,
            graduationDate: plan.graduationDate,
            tenantId,
            organizationId: (_d = plan.organization) === null || _d === void 0 ? void 0 : _d.id,
        },
        degreePlanTermDtos: sortedTerms.map((degreePlanTerm) => ({
            degreePlanTerm: {
                degreePlanTermId: degreePlanTerm.id,
                degreePlanId: plan.id,
                termId: degreePlanTerm.term.id,
                position: degreePlanTerm.position,
            },
            degreePlanTermCourseDtos: degreePlanTerm.courses.map((degreePlanTermCourse) => ({
                degreePlanTermCourse: {
                    degreePlanTermId: degreePlanTerm.id,
                    courseId: degreePlanTermCourse.course.id,
                    failed: degreePlanTermCourse.failed,
                },
            })),
        })),
    };
};
const buildCohortUpdatePayload = (plan) => {
    var _a, _b, _c, _d;
    const sortedTerms = [...(plan.terms || [])].sort((a, b) => a.position - b.position);
    const tenantId = plan.tenantId || ((_a = plan.organization) === null || _a === void 0 ? void 0 : _a.tenantId) || ((_b = plan.cohort) === null || _b === void 0 ? void 0 : _b.tenantId);
    return {
        degreePlan: {
            degreePlanId: plan.id,
            cohortId: (_c = plan.cohort) === null || _c === void 0 ? void 0 : _c.id,
            type: plan.type,
            graduationDate: plan.graduationDate,
            tenantId,
            organizationId: (_d = plan.organization) === null || _d === void 0 ? void 0 : _d.id,
        },
        degreePlanTermDtos: sortedTerms.map((degreePlanTerm) => ({
            degreePlanTerm: {
                degreePlanTermId: degreePlanTerm.id,
                degreePlanId: plan.id,
                termId: degreePlanTerm.term.id,
                position: degreePlanTerm.position,
            },
            degreePlanTermCourseDtos: degreePlanTerm.courses.map((degreePlanTermCourse) => ({
                degreePlanTermCourse: {
                    degreePlanTermId: degreePlanTerm.id,
                    courseId: degreePlanTermCourse.course.id,
                    failed: degreePlanTermCourse.failed,
                },
            })),
        })),
    };
};
exports.degreePlanApi = {
    /**
     * Fetch all degree plans for an organization
     */
    getDegreePlansByOrg: (orgId) => {
        (0, validation_1.validateId)(orgId, 'Organization ID');
        return apiClient_1.apiClient.get(`/degreePlan/organization/${orgId}`).then(data => data.map(apiMappers_1.mapDegreePlan));
    },
    /**
     * Fetch cohort plans only for an organization
     */
    getCohortPlansByOrg: (orgId) => {
        (0, validation_1.validateId)(orgId, 'Organization ID');
        return apiClient_1.apiClient.get(`/degreePlan/organization/cohorts/${orgId}`).then(data => data.map(apiMappers_1.mapDegreePlan));
    },
    /**
     * Fetch a single degree plan by ID
     */
    getDegreePlanById: (id) => {
        (0, validation_1.validateId)(id, 'Degree Plan ID');
        return apiClient_1.apiClient.get(`/degreePlan/${id}`).then(apiMappers_1.mapDegreePlan);
    },
    /**
     * Fetch degree plan by cohort ID.
     * The API returns an array of plans for the cohort; we find the one with type === 'cohort'.
     */
    getDegreePlanByCohortId: (cohortId) => {
        (0, validation_1.validateId)(cohortId, 'Cohort ID');
        return apiClient_1.apiClient.get(`/degreePlan/cohort/${cohortId}`).then(raw => {
            var _a;
            if (Array.isArray(raw)) {
                const plans = raw.map(apiMappers_1.mapDegreePlan);
                const cohortPlan = (_a = plans.find(dp => dp.type === 'cohort')) !== null && _a !== void 0 ? _a : plans[0];
                if (!cohortPlan)
                    throw new Error('Cohort degree plan not found');
                return cohortPlan;
            }
            return (0, apiMappers_1.mapDegreePlan)(raw);
        });
    },
    /**
     * Fetch student plans by cohort ID
     */
    getStudentPlansByCohortId: (cohortId) => {
        (0, validation_1.validateId)(cohortId, 'Cohort ID');
        return apiClient_1.apiClient.get(`/degreePlan/student/cohort/${cohortId}`).then(data => data.map(apiMappers_1.mapDegreePlan));
    },
    /**
     * Fetch degree plan(s) by student program ID (returns array, typically with one plan)
     */
    getDegreePlanByStudentProgramId: (studentProgramId) => {
        (0, validation_1.validateId)(studentProgramId, 'Student Program ID');
        return apiClient_1.apiClient.get(`/degreePlan/student/${studentProgramId}`).then(data => data.map(apiMappers_1.mapDegreePlan));
    },
    /**
     * Update a student degree plan
     */
    updateStudentPlan: (plan) => apiClient_1.apiClient.put('/degreePlan/student', buildStudentUpdatePayload(plan)).then(apiMappers_1.mapDegreePlan),
    /**
     * Update a cohort degree plan
     */
    updateCohortPlan: (plan) => apiClient_1.apiClient.put('/degreePlan/cohort', buildCohortUpdatePayload(plan)).then(apiMappers_1.mapDegreePlan),
    /**
     * Delete a student degree plan
     */
    deleteStudentPlan: (id) => {
        (0, validation_1.validateId)(id, 'Degree Plan ID');
        return apiClient_1.apiClient.delete(`/degreePlan/student/${id}`);
    },
    /**
     * Create degree plans in bulk (SNAP feature)
     */
    createBulkPlans: (plans) => apiClient_1.apiClient.post('/degreePlan', plans).then(data => data.map(apiMappers_1.mapDegreePlan)),
    /**
     * Create SNAP plans with full payload (cohort + students + plan).
     * The API returns a single DegreePlan (the cohort plan created); we discard
     * the response body since the caller navigates away on success.
     */
    createSnapPlans: (payload) => apiClient_1.apiClient.post('/degreePlan', payload).then(() => { }),
    /**
     * Add additional student plans to an existing cohort.
     * The backend returns a single CohortSnapDto (not an array).
     */
    createAdditionalStudentPlansInCohort: (cohortPlan, studentIds) => apiClient_1.apiClient
        .post('/degreePlan/cohort/add', buildExistingCohortAddPayload(cohortPlan, studentIds))
        .then(() => { }),
};
